import React, { useState, useEffect } from 'react';
import { ProcessStep } from '../../services/ProcessStepService';
import { X, Plus, Trash2 } from 'lucide-react';

interface ConfigurationData {
  countries: Array<{ id: string; name: string }>;
  legalEntities: Array<{ id: string; name: string }>;
  supportingModels: Array<{ id: string; name: string }>;
  thirdPartyBusinessTypes: Array<{ id: string; name: string }>;
}

interface ProcessStepMetadataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (stepData: Partial<ProcessStep>) => Promise<void>;
  configurations: ConfigurationData;
  initialData?: Partial<ProcessStep>;
}

export const ProcessStepMetadataModal: React.FC<ProcessStepMetadataModalProps> = ({
  isOpen,
  onClose,
  onSave,
  configurations,
  initialData = {}
}) => {
  const [formData, setFormData] = useState({
    // Core identifiers
    process_step_name: '',
    description: '',
    process_step_id: '',
    
    // Location metadata
    performing_country: [] as string[],
    performing_legal_entity: [] as string[],
    affected_country: [] as string[],
    affected_legal_entity: [] as string[],
    
    // Process characteristics
    process_step_type: '' as 'Automated' | 'Semi-automated' | 'Manual' | '',
    supporting_models: [] as string[],
    non_model_tools: '',
    
    // Third party information
    third_party_support_type: '' as 'All' | 'Partial' | 'None' | '',
    third_party_relationship_type: '',
    external_parties_listing: '',
    
    // Internal parties
    performer_relier_relationship: false,
    relier: '',
    relier_legal_entity: '',
    performer_relier_details: '',
    performer_relier_rationale: '',
    
    // Additional metadata
    supporting_applications: [] as string[],
    policy: '',
    procedure: '',
    
    // Swimlane information
    swimlane_info: {} as any
  });

  const [customEntries, setCustomEntries] = useState({
    supportingModel: '',
    supportingApplication: ''
  });

  const [visibleFields, setVisibleFields] = useState({
    core: true,
    location: false,
    processChar: false,
    thirdParty: false,
    internalParties: false,
    additional: false,
    swimlane: false
  });

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
        performing_country: Array.isArray(initialData.performing_country) 
          ? initialData.performing_country 
          : initialData.performing_country ? [initialData.performing_country] : [],
        performing_legal_entity: Array.isArray(initialData.performing_legal_entity) 
          ? initialData.performing_legal_entity 
          : initialData.performing_legal_entity ? [initialData.performing_legal_entity] : [],
        affected_country: Array.isArray(initialData.affected_country) 
          ? initialData.affected_country 
          : initialData.affected_country ? [initialData.affected_country] : [],
        affected_legal_entity: Array.isArray(initialData.affected_legal_entity) 
          ? initialData.affected_legal_entity 
          : initialData.affected_legal_entity ? [initialData.affected_legal_entity] : [],
        supporting_models: Array.isArray(initialData.supporting_models) 
          ? initialData.supporting_models 
          : [],
        supporting_applications: Array.isArray(initialData.supporting_applications) 
          ? initialData.supporting_applications 
          : []
      }));
    }
  }, [initialData]);

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMultiSelectChange = (field: keyof typeof formData, value: string, checked: boolean) => {
    setFormData(prev => {
      const currentArray = prev[field] as string[];
      if (checked) {
        return {
          ...prev,
          [field]: [...currentArray, value]
        };
      } else {
        return {
          ...prev,
          [field]: currentArray.filter(item => item !== value)
        };
      }
    });
  };

  const handleAddCustomSupportingModel = () => {
    if (customEntries.supportingModel.trim()) {
      handleMultiSelectChange('supporting_models', customEntries.supportingModel.trim(), true);
      setCustomEntries(prev => ({ ...prev, supportingModel: '' }));
    }
  };

  const handleAddCustomApplication = () => {
    if (customEntries.supportingApplication.trim()) {
      handleMultiSelectChange('supporting_applications', customEntries.supportingApplication.trim(), true);
      setCustomEntries(prev => ({ ...prev, supportingApplication: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Failed to save process step:', error);
    }
  };

  const toggleFieldSection = (section: keyof typeof visibleFields) => {
    setVisibleFields(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const CollapsibleSection: React.FC<{
    title: string;
    sectionKey: keyof typeof visibleFields;
    children: React.ReactNode;
  }> = ({ title, sectionKey, children }) => (
    <div className="border border-gray-200 rounded-lg mb-4">
      <button
        type="button"
        onClick={() => toggleFieldSection(sectionKey)}
        className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-t-lg flex items-center justify-between"
      >
        <span className="font-medium text-[#221551]">{title}</span>
        <span className={`transform transition-transform ${visibleFields[sectionKey] ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>
      {visibleFields[sectionKey] && (
        <div className="p-4 border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b bg-[#221551] text-white">
          <h2 className="text-xl font-semibold">
            {initialData?.id ? 'Edit Process Step' : 'Add Process Step'}
          </h2>
          <button onClick={onClose} className="text-white hover:text-gray-300">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="max-h-[calc(90vh-120px)] overflow-y-auto">
          <div className="p-6 space-y-4">
            {/* Core Fields (Always Visible) */}
            <CollapsibleSection title="Core Information" sectionKey="core">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Process Step Name *
                  </label>
                  <input
                    type="text"
                    value={formData.process_step_name}
                    onChange={(e) => handleInputChange('process_step_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Process Step ID
                  </label>
                  <input
                    type="text"
                    value={formData.process_step_id}
                    onChange={(e) => handleInputChange('process_step_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    placeholder="Auto-generated if empty"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                  />
                </div>
              </div>
            </CollapsibleSection>

            {/* Location Metadata */}
            <CollapsibleSection title="Location Metadata" sectionKey="location">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Performing Country
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                    {configurations.countries.map((country) => (
                      <label key={country.id} className="flex items-center mb-1">
                        <input
                          type="checkbox"
                          checked={formData.performing_country.includes(country.id)}
                          onChange={(e) => handleMultiSelectChange('performing_country', country.id, e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm">{country.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Performing Legal Entity
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                    {configurations.legalEntities.map((entity) => (
                      <label key={entity.id} className="flex items-center mb-1">
                        <input
                          type="checkbox"
                          checked={formData.performing_legal_entity.includes(entity.id)}
                          onChange={(e) => handleMultiSelectChange('performing_legal_entity', entity.id, e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm">{entity.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Affected Country
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                    {configurations.countries.map((country) => (
                      <label key={country.id} className="flex items-center mb-1">
                        <input
                          type="checkbox"
                          checked={formData.affected_country.includes(country.id)}
                          onChange={(e) => handleMultiSelectChange('affected_country', country.id, e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm">{country.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Affected Legal Entity
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                    {configurations.legalEntities.map((entity) => (
                      <label key={entity.id} className="flex items-center mb-1">
                        <input
                          type="checkbox"
                          checked={formData.affected_legal_entity.includes(entity.id)}
                          onChange={(e) => handleMultiSelectChange('affected_legal_entity', entity.id, e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm">{entity.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Process Characteristics */}
            <CollapsibleSection title="Process Characteristics" sectionKey="processChar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Process Step Type
                  </label>
                  <select
                    value={formData.process_step_type}
                    onChange={(e) => handleInputChange('process_step_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                  >
                    <option value="">Select Type</option>
                    <option value="Automated">Automated</option>
                    <option value="Semi-automated">Semi-automated</option>
                    <option value="Manual">Manual</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Non-Model Tools
                  </label>
                  <input
                    type="text"
                    value={formData.non_model_tools}
                    onChange={(e) => handleInputChange('non_model_tools', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    placeholder="Describe non-model tools used"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supporting Models
                  </label>
                  <div className="space-y-2">
                    <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                      {configurations.supportingModels.map((model) => (
                        <label key={model.id} className="flex items-center mb-1">
                          <input
                            type="checkbox"
                            checked={formData.supporting_models.includes(model.id)}
                            onChange={(e) => handleMultiSelectChange('supporting_models', model.id, e.target.checked)}
                            className="mr-2"
                          />
                          <span className="text-sm">{model.name}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={customEntries.supportingModel}
                        onChange={(e) => setCustomEntries(prev => ({ ...prev, supportingModel: e.target.value }))}
                        placeholder="Add custom model"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                      />
                      <button
                        type="button"
                        onClick={handleAddCustomSupportingModel}
                        className="px-4 py-2 bg-[#57C8F4] text-white rounded-md hover:bg-[#30A8CF]"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    {formData.supporting_models.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.supporting_models.map((model) => (
                          <span key={model} className="inline-flex items-center gap-1 px-2 py-1 bg-[#C1E5F5] text-[#221551] rounded text-xs">
                            {configurations.supportingModels.find(m => m.id === model)?.name || model}
                            <button
                              type="button"
                              onClick={() => handleMultiSelectChange('supporting_models', model, false)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Third Party Information */}
            <CollapsibleSection title="Third Party Information" sectionKey="thirdParty">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Third Party Support Type
                  </label>
                  <select
                    value={formData.third_party_support_type}
                    onChange={(e) => handleInputChange('third_party_support_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                  >
                    <option value="">Select Type</option>
                    <option value="All">All</option>
                    <option value="Partial">Partial</option>
                    <option value="None">None</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Third Party Business Relationship Type
                  </label>
                  <select
                    value={formData.third_party_relationship_type}
                    onChange={(e) => handleInputChange('third_party_relationship_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                  >
                    <option value="">Select Relationship Type</option>
                    {configurations.thirdPartyBusinessTypes.map((type) => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    External Parties Listing
                  </label>
                  <textarea
                    value={formData.external_parties_listing}
                    onChange={(e) => handleInputChange('external_parties_listing', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    placeholder="Comma or tab separated list of external parties (TLM IDs)"
                  />
                </div>
              </div>
            </CollapsibleSection>

            {/* Internal Parties */}
            <CollapsibleSection title="Internal Parties" sectionKey="internalParties">
              <div className="space-y-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.performer_relier_relationship}
                    onChange={(e) => handleInputChange('performer_relier_relationship', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Performer-Relier Relationship Exists
                  </span>
                </label>

                {formData.performer_relier_relationship && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6 border-l-2 border-[#57C8F4]">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Relier
                      </label>
                      <input
                        type="text"
                        value={formData.relier}
                        onChange={(e) => handleInputChange('relier', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Relier Legal Entity
                      </label>
                      <select
                        value={formData.relier_legal_entity}
                        onChange={(e) => handleInputChange('relier_legal_entity', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                      >
                        <option value="">Select Legal Entity</option>
                        {configurations.legalEntities.map((entity) => (
                          <option key={entity.id} value={entity.id}>{entity.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Performer-Relier Details
                      </label>
                      <textarea
                        value={formData.performer_relier_details}
                        onChange={(e) => handleInputChange('performer_relier_details', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rationale
                      </label>
                      <textarea
                        value={formData.performer_relier_rationale}
                        onChange={(e) => handleInputChange('performer_relier_rationale', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                      />
                    </div>
                  </div>
                )}
              </div>
            </CollapsibleSection>

            {/* Additional Metadata */}
            <CollapsibleSection title="Additional Information" sectionKey="additional">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supporting Applications (Central IDs)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customEntries.supportingApplication}
                      onChange={(e) => setCustomEntries(prev => ({ ...prev, supportingApplication: e.target.value }))}
                      placeholder="Add application ID"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    />
                    <button
                      type="button"
                      onClick={handleAddCustomApplication}
                      className="px-4 py-2 bg-[#57C8F4] text-white rounded-md hover:bg-[#30A8CF]"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  {formData.supporting_applications.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.supporting_applications.map((app, index) => (
                        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-[#C1E5F5] text-[#221551] rounded text-xs">
                          {app}
                          <button
                            type="button"
                            onClick={() => {
                              const newApps = [...formData.supporting_applications];
                              newApps.splice(index, 1);
                              handleInputChange('supporting_applications', newApps);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Policy
                  </label>
                  <textarea
                    value={formData.policy}
                    onChange={(e) => handleInputChange('policy', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    placeholder="Associated policies"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Procedure
                  </label>
                  <textarea
                    value={formData.procedure}
                    onChange={(e) => handleInputChange('procedure', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    placeholder="Associated procedures"
                  />
                </div>
              </div>
            </CollapsibleSection>
          </div>

          <div className="flex gap-4 p-6 border-t bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-[#57C8F4] text-white rounded-md hover:bg-[#30A8CF] transition-colors"
            >
              {initialData?.id ? 'Update Step' : 'Create Step'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};